import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Upload, Trash2, Save, FileText, RefreshCw, Lock, Globe, BookOpen, Layers, Palette, ImagePlus } from 'lucide-react';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const LEVEL_BADGES = {
  'resume-supported': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'flyer-supported': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'document-attached': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'steve-provided': 'bg-slate-100 text-slate-600 border-slate-200',
  'needs-document': 'bg-amber-50 text-amber-700 border-amber-200',
  'private-source': 'bg-slate-100 text-slate-500 border-slate-200',
};

export const SupportBadge = ({ level, labels }) => {
  if (!level) return null;
  const label = labels?.[level] || level;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-bold ${LEVEL_BADGES[level] || 'bg-slate-100 text-slate-600 border-slate-200'}`} data-testid={`support-badge-${level}`}>
      {label}
    </span>
  );
};

const FIELD_CLS = 'h-10';

export default function AdminStoryMedia() {
  const [data, setData] = useState(null);
  const [stories, setStories] = useState([]);
  const [sections, setSections] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [uploadMeta, setUploadMeta] = useState({ title: '', category: 'other', support_level: 'steve-provided', caption: '', alt_text: '', source_note: '', attached_story_id: '', attached_section_id: '' });
  const uploadInput = useRef(null);
  const replaceInputs = useRef({});

  const load = useCallback(() => {
    api.get('/admin/story-media').then((r) => setData(r.data)).catch(() => toast.error('Failed to load media'));
    api.get('/admin/stories').then((r) => setStories(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
    api.get('/pages/experience').then((r) => setSections(r.data?.sections || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(uploadMeta).forEach(([k, v]) => fd.append(k, v || ''));
      await api.post('/admin/story-media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded — saved as Private. Turn on “Public” when Steve approves it.');
      setUploadMeta((m) => ({ ...m, title: '', caption: '', alt_text: '', source_note: '' }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (uploadInput.current) uploadInput.current.value = '';
    }
  };

  const saveItem = async (item) => {
    setSavingId(item.id);
    try {
      await api.put(`/admin/story-media/${item.id}`, item);
      toast.success('Media saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId('');
    }
  };

  const setItem = (id, key, value) => {
    setData((d) => ({ ...d, items: d.items.map((i) => (i.id === id ? { ...i, [key]: value } : i)) }));
  };

  const replaceFile = async (id, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/admin/story-media/${id}/replace`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File replaced');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replace failed');
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Remove this media item and its file?')) return;
    try {
      await api.delete(`/admin/story-media/${id}`);
      toast.success('Media removed');
      load();
    } catch (err) {
      toast.error('Remove failed');
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const cats = data.categories || {};
  const levels = data.support_levels || {};
  const items = (data.items || []).filter((i) => filterCat === 'all' || i.category === filterCat);
  const storyOptions = [...stories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const attachSelects = (value, onChange, list, placeholder, testid) => (
    <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
      <SelectTrigger className={FIELD_CLS} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value="none">— Not attached —</SelectItem>
        {list.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  const storyList = storyOptions.map((s) => ({ value: s.id, label: s.title }));
  const sectionList = sections.map((s) => ({ value: s.id, label: s.title }));

  return (
    <div data-testid="admin-story-media-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Story Media Manager</h1>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/designs" data-testid="story-media-link-designs"><Palette className="h-4 w-4 mr-1" /> Design Manager</Link></Button>
          <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/stories" data-testid="story-media-link-stories"><BookOpen className="h-4 w-4 mr-1" /> Steve's Story</Link></Button>
          <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/pages" data-testid="story-media-link-pages"><Layers className="h-4 w-4 mr-1" /> Experience Page</Link></Button>
        </div>
      </div>
      <p className="text-slate-500 text-sm mb-4">Photos, clippings, logos, screenshots, and proof documents for Steve's Story and the Experience page — all in one place.</p>

      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5 text-sm text-amber-800 mb-6 flex items-start gap-2" data-testid="story-media-privacy-note">
        <Lock className="h-4 w-4 mt-0.5 shrink-0" />
        <span><strong>New uploads are Private by default.</strong> Nothing appears publicly until Steve turns on the Public toggle. Documents are never auto-published. No restricted CKL imagery, no Google Maps screenshots, no private resume details.</span>
      </div>

      {(() => {
        const sheridanStory = stories.find((s) => (s.title || '').includes('Sheridan Mobile Computing'));
        if (!sheridanStory) return null;
        const hasMedia = (data.items || []).some((i) => i.attached_story_id === sheridanStory.id);
        if (hasMedia) return null;
        return (
          <div className="rounded-xl bg-[#1a2744] p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="sheridan-photo-needed-note">
            <div className="flex items-start gap-2.5 flex-1 min-w-0">
              <ImagePlus className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
              <p className="text-sm text-slate-200 leading-relaxed">
                <strong className="text-white">Photo needed:</strong> Upload approved furniture, classroom, or award image here
                for the story <strong className="text-[#C9A84C]">The Sheridan Mobile Computing Furniture Story</strong>.
                It stays private until Steve approves and turns on Public.
              </p>
            </div>
            <Button
              variant="outline"
              className="h-10 border-[#C9A84C] bg-transparent text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:text-[#C9A84C] font-bold shrink-0"
              onClick={() => {
                setUploadMeta((m) => ({ ...m, category: 'sheridan-furniture', attached_story_id: sheridanStory.id }));
                toast.info('Upload form set to the Sheridan category and attached to the Sheridan story.');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              data-testid="sheridan-photo-quick-setup-btn"
            >
              Set up Sheridan upload
            </Button>
          </div>
        );
      })()}

      {/* Upload */}
      <Card className="mb-8" data-testid="story-media-upload-card">
        <CardContent className="p-5">
          <p className="font-serif font-bold text-lg text-[#1a2744] mb-4">Add new media</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Title</label>
              <Input value={uploadMeta.title} onChange={(e) => setUploadMeta((m) => ({ ...m, title: e.target.value }))} className={FIELD_CLS} data-testid="story-media-upload-title" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Category</label>
              <Select value={uploadMeta.category} onValueChange={(v) => setUploadMeta((m) => ({ ...m, category: v }))}>
                <SelectTrigger className={FIELD_CLS} data-testid="story-media-upload-category"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{Object.entries(cats).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Support level</label>
              <Select value={uploadMeta.support_level} onValueChange={(v) => setUploadMeta((m) => ({ ...m, support_level: v }))}>
                <SelectTrigger className={FIELD_CLS} data-testid="story-media-upload-level"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(levels).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Source note</label>
              <Input value={uploadMeta.source_note} onChange={(e) => setUploadMeta((m) => ({ ...m, source_note: e.target.value }))} className={FIELD_CLS} data-testid="story-media-upload-source" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Caption (shown under the image if public)</label>
              <Input value={uploadMeta.caption} onChange={(e) => setUploadMeta((m) => ({ ...m, caption: e.target.value }))} className={FIELD_CLS} data-testid="story-media-upload-caption" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Alt text (accessibility)</label>
              <Input value={uploadMeta.alt_text} onChange={(e) => setUploadMeta((m) => ({ ...m, alt_text: e.target.value }))} className={FIELD_CLS} data-testid="story-media-upload-alt" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Attach to story block (optional)</label>
              {attachSelects(uploadMeta.attached_story_id, (v) => setUploadMeta((m) => ({ ...m, attached_story_id: v })), storyList, 'Choose story', 'story-media-upload-story')}
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Attach to experience section (optional)</label>
              {attachSelects(uploadMeta.attached_section_id, (v) => setUploadMeta((m) => ({ ...m, attached_section_id: v })), sectionList, 'Choose section', 'story-media-upload-section')}
            </div>
          </div>
          <div className="mt-4">
            <input ref={uploadInput} type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.doc,.docx" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="story-media-upload-input" />
            <Button onClick={() => uploadInput.current?.click()} disabled={uploading} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="story-media-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Choose file & upload (saved Private)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Library */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="font-serif font-bold text-lg text-[#1a2744]">Media library ({items.length})</p>
        <div className="w-64">
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className={FIELD_CLS} data-testid="story-media-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(cats).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="story-media-empty">No media yet. Upload approved photos, clippings, or proof documents above — they stay private until Steve makes them public.</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {items.map((i) => (
            <Card key={i.id} data-testid="story-media-item">
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-full sm:w-40 shrink-0">
                    {i.kind === 'image' ? (
                      <img src={fileUrl(i.file_url)} alt={i.alt_text || i.title} className="rounded-lg w-full h-28 object-cover border border-slate-200" />
                    ) : (
                      <a href={fileUrl(i.file_url)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center h-28 rounded-lg bg-slate-50 border border-slate-200 text-slate-500 hover:text-[#1a2744]">
                        <FileText className="h-8 w-8" />
                      </a>
                    )}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {i.public ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-bold" data-testid="story-media-public-badge"><Globe className="h-3 w-3" /> Public</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 bg-slate-100 text-slate-600 text-[11px] font-bold" data-testid="story-media-private-badge"><Lock className="h-3 w-3" /> Private</span>
                      )}
                      <SupportBadge level={i.support_level} labels={levels} />
                    </div>
                  </div>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 mb-1">Title</label>
                      <Input value={i.title || ''} onChange={(e) => setItem(i.id, 'title', e.target.value)} className={FIELD_CLS} data-testid="story-media-item-title" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 mb-1">Category</label>
                      <Select value={i.category || 'other'} onValueChange={(v) => setItem(i.id, 'category', v)}>
                        <SelectTrigger className={FIELD_CLS}><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-72">{Object.entries(cats).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 mb-1">Caption</label>
                      <Input value={i.caption || ''} onChange={(e) => setItem(i.id, 'caption', e.target.value)} className={FIELD_CLS} />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 mb-1">Alt text</label>
                      <Input value={i.alt_text || ''} onChange={(e) => setItem(i.id, 'alt_text', e.target.value)} className={FIELD_CLS} />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 mb-1">Source note</label>
                      <Input value={i.source_note || ''} onChange={(e) => setItem(i.id, 'source_note', e.target.value)} className={FIELD_CLS} />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 mb-1">Support level</label>
                      <Select value={i.support_level || 'steve-provided'} onValueChange={(v) => setItem(i.id, 'support_level', v)}>
                        <SelectTrigger className={FIELD_CLS} data-testid="story-media-item-level"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(levels).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 mb-1">Attached story block</label>
                      {attachSelects(i.attached_story_id, (v) => setItem(i.id, 'attached_story_id', v), storyList, 'Choose story', 'story-media-item-story')}
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 mb-1">Attached experience section</label>
                      {attachSelects(i.attached_section_id, (v) => setItem(i.id, 'attached_section_id', v), sectionList, 'Choose section', 'story-media-item-section')}
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                      <Switch checked={!!i.public} onCheckedChange={(v) => setItem(i.id, 'public', v)} data-testid="story-media-item-public" /> Public
                    </label>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                      <Switch checked={i.visible !== false} onCheckedChange={(v) => setItem(i.id, 'visible', v)} data-testid="story-media-item-visible" /> Show
                    </label>
                    <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-600">
                      Order
                      <Input type="number" value={i.order ?? 0} onChange={(e) => setItem(i.id, 'order', parseInt(e.target.value || '0', 10))} className="h-9 w-20" data-testid="story-media-item-order" />
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => saveItem(i)} disabled={savingId === i.id} size="sm" className="h-9 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="story-media-item-save">
                      {savingId === i.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save
                    </Button>
                    <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.doc,.docx" className="hidden" ref={(el) => { replaceInputs.current[i.id] = el; }} onChange={(e) => replaceFile(i.id, e.target.files?.[0])} />
                    <Button onClick={() => replaceInputs.current[i.id]?.click()} variant="outline" size="sm" className="h-9 font-bold" data-testid="story-media-item-replace">
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Replace file
                    </Button>
                    <Button onClick={() => removeItem(i.id)} variant="outline" size="sm" className="h-9 font-bold text-red-600 border-red-200 hover:bg-red-50" data-testid="story-media-item-remove">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
